/**
 * TasksSettingsTab - Aba "Tarefas" das Configurações do tenant
 *
 * Permite habilitar/desabilitar os submódulos que reaproveitam a base de
 * Tarefas: Retenção e Jurídico. Ambos vêm desligados por padrão ao contratar
 * o módulo de Tarefas (não exige contratação adicional no plano).
 *
 * Quando um submódulo é habilitado, o menu lateral correspondente aparece e a
 * aba própria dele passa a existir em Perfis de Permissão.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial
 */

import { LifeBuoy, Scale, Info } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  SettingsCard,
  SettingsInfoBanner,
  SettingsSectionHeader,
} from '@/components/tenant/SettingsInfoRow';
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';

/** Aba de configurações do módulo de tarefas (submódulos Retenção/Jurídico) */
export function TasksSettingsTab() {
  const { tasksEnabled, retentionEnabled, legalEnabled, isLoading, updateSettings } =
    useTasksModuleSettings();

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-lg" />;
  }

  return (
    <div className="space-y-6">
      <SettingsCard>
        <SettingsSectionHeader
          eyebrow="Módulo"
          title="Tarefas"
          description="Submódulos que reaproveitam toda a estrutura de Tarefas (categorias, fases, kanban e dashboards), porém com dados totalmente separados."
        />

        {/* ---------------- Retenção ---------------- */}
        <div className="flex items-center justify-between gap-4 border-b border-border/60 px-6 py-4">
          <div className="flex items-start gap-3">
            <LifeBuoy className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Habilitar Retenção</p>
              <p className="text-muted-foreground">
                Exibe o menu lateral Retenção e libera a aba Retenção nos perfis de permissão.
              </p>
            </div>
          </div>
          <Switch
            checked={retentionEnabled}
            disabled={!tasksEnabled || updateSettings.isPending}
            onCheckedChange={(checked) => updateSettings.mutate({ retentionEnabled: checked })}
          />
        </div>

        {/* ---------------- Jurídico ---------------- */}
        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-start gap-3">
            <Scale className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Habilitar Jurídico</p>
              <p className="text-muted-foreground">
                Exibe o menu lateral Jurídico e libera a aba Jurídico nos perfis de permissão.
              </p>
            </div>
          </div>
          <Switch
            checked={legalEnabled}
            disabled={!tasksEnabled || updateSettings.isPending}
            onCheckedChange={(checked) => updateSettings.mutate({ legalEnabled: checked })}
          />
        </div>

        <div className="px-6 pb-5">
          <SettingsInfoBanner icon={Info}>
            {tasksEnabled
              ? 'Após habilitar, defina em Perfis de Permissão quais usuários podem acessar cada menu do submódulo. Desligar um submódulo apenas oculta as telas — as permissões já configuradas são preservadas.'
              : 'O módulo de Tarefas precisa estar habilitado no plano para configurar Retenção e Jurídico.'}
          </SettingsInfoBanner>
        </div>
      </SettingsCard>
    </div>
  );
}
